// ============================================================================
// LES LISTES SERVIES PAR UN TIERS — ce que l'écran en montre, décidé hors du rendu.
//
// UTILITÉ : un réglage dont les choix viennent d'un fournisseur (les gabarits de coupon Eldowallet) se
// règle à deux endroits — la ligne partagée des réglages et la carte du wallet. Ce qu'une réponse du
// serveur devient, ce qu'une liste vide veut dire et quand la liste se redemande s'écrivent ici UNE fois.
//
// ⚠️ MODULE PUR, SANS REACT NI API, et c'est ce qui le fait exister à part : le registre des
// intégrations lit le modèle de la carte wallet, qui lit ce module. Posées dans l'écran des réglages,
// ces fonctions auraient fait importer au registre un écran qui importe lui-même le registre — une
// boucle de chargement qui rend le registre `undefined` au démarrage.
// ============================================================================
import type { ReglageVue, SourceListe } from './parametres-modele';
import { key, t, type Translatable } from '../i18n';

/**
 * UNE LISTE SERVIE PAR UN TIERS — ses choix, ou le MOTIF pour lequel elle n'a pas pu être servie.
 * ⚠️ 🔴 LES DEUX SONT PORTÉS, jamais l'un à la place de l'autre : une liste vide et une liste
 * refusée se réparent à des endroits différents, et l'écran ne peut les distinguer que si le motif
 * remonte (§23.0).
 */
export interface ServedList {
  options: ServedOption[];
  error?: string;
}

/**
 * Un choix servi : son identifiant chez le tiers, son nom, et sa COULEUR quand le tiers en donne une.
 * ⚠️ `null` = aucune couleur reconnue : l'écran dessine une pastille neutre, il n'en invente pas.
 */
export interface ServedOption {
  id: string;
  name: string;
  color: string | null;
}

/** Les listes servies, par source — `null` = pas encore demandée. */
export type ServedLists = Record<SourceListe, ServedList | null>;

/**
 * Ce qu'une réponse du serveur devient à l'écran.
 * ⚠️ LE MOTIF DU SERVEUR EST RENDU TEL QUEL — c'est lui qui dit si la clé manque, si le fournisseur
 * a refusé, ou si le compte ne porte simplement aucun gabarit. Un refus SANS motif le dit aussi,
 * plutôt que de passer pour une liste vide.
 */
export function servedListFrom(r: {
  ok: boolean; templates?: { id: string; name: string; color?: string | null }[] | null; error?: string | null;
}): ServedList {
  if (r.ok) return { options: (r.templates ?? []).map((o) => ({ id: o.id, name: o.name, color: o.color ?? null })) };
  return { options: [], error: r.error ?? t('motif non rendu par le serveur') };
}

/**
 * CE QU'UNE LISTE VIDE VEUT DIRE, par source — dit à l'écran au lieu d'un menu qui n'offrirait que « Aucun ».
 * ⚠️ Un `Record` sur l'union des sources : ajouter une source sans dire ce que son vide signifie ne compile pas.
 */
const EMPTY_NOTES: Record<SourceListe, Translatable> = {
  // ⚠️ « au prochain test de la connexion », et non « il apparaîtra ici » tout court : la liste se relit
  //    quand la connexion se relit (test, clé posée, magasin changé), pas à chaque ouverture du menu.
  wallet_coupon_templates: key('Aucun gabarit de coupon sur votre compte Eldowallet. Créez-en un dans Eldowallet → Espace pro → Coupons : il apparaîtra ici au prochain test de la connexion.'),
};

/** Ce que le choix doit montrer, décidé hors du rendu pour être éprouvé sans navigateur. */
export type ServedListView =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty'; note: Translatable; unknownValue: string | null }
  | { kind: 'choices'; options: ServedOption[]; unknownValue: string | null };

/**
 * L'état d'un choix dans une liste servie : en chargement, refusée (motif du serveur), VIDE (note de la
 * source), ou des choix. ⚠️ Une valeur enregistrée absente de la liste est gardée, vide ou non — sinon le
 * premier enregistrement du voisin l'effacerait sans que personne ne l'ait demandé.
 */
export function servedListView(served: ServedList | null, value: string, source: SourceListe): ServedListView {
  if (served === null) return { kind: 'loading' };
  if (served.error) return { kind: 'error', message: served.error };
  const unknownValue = value !== '' && !served.options.some((o) => o.id === value) ? value : null;
  if (served.options.length === 0) return { kind: 'empty', note: EMPTY_NOTES[source], unknownValue };
  return { kind: 'choices', options: served.options, unknownValue };
}

/**
 * La clé qui décide QUAND les listes se redemandent : les sources nécessaires ET `reloadKey` — `null` = rien
 * à demander (aucun magasin actif, ou aucun réglage ne réclame de liste).
 * ⚠️ 🔴 SANS `reloadKey`, LA LISTE ÉTAIT LUE UNE FOIS À L'OUVERTURE : la clé Eldowallet posée ensuite relisait
 *    la connexion (« connecté ») mais laissait la liste sur l'erreur d'AVANT la clé — « Aucun jeton ».
 */
export function servedListsEffectKey(settings: readonly ReglageVue[], enabled: boolean, reloadKey: string): string | null {
  const needed = neededSources(settings).join(',');
  return enabled && needed ? `${needed}|${reloadKey}` : null;
}

/** Les sources que ces réglages réclament — dédoublonnées, dans un ordre stable. */
export function neededSources(settings: readonly ReglageVue[]): SourceListe[] {
  return Array.from(new Set(settings.flatMap((r) => (r.forme === 'liste' ? [r.source] : [])))).sort();
}
